import { SvgIcon } from '@cinch-labs/design-system'
import { Actions, Context, SearchVehicleImage } from '@cinch-nx/data-search'
import { sendDigitalDataEvent } from '@cinch-nx/shared-analytics'
import { HoldingImage } from '@cinch-nx/shared-ui'
import { datadogRum } from '@datadog/browser-rum'
import cx from 'classnames'
import Image from 'next/legacy/image'
import { useEffect, useRef, useState } from 'react'
import SwiperCore, { A11y, Navigation, Pagination } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import styles from './vehicle-image-carousel.module.css'

export type AdditionalImageTitle =
  | 'Front driver side'
  | 'Rear passenger side'
  | 'Interior front'
  | 'Interior back'
  | 'Boot'

export type VehicleImageCarouselProps = {
  images: SearchVehicleImage[]
  isPending?: boolean
  isAvailable?: boolean
  productDetailUrl: string
  ctaTitle: string
  imageWithMorePhotosCta?: AdditionalImageTitle
  imagesToDisplay?: AdditionalImageTitle[]
}

// eslint-disable-next-line react-hooks/rules-of-hooks
SwiperCore.use([A11y, Navigation, Pagination])

const handleNavigateButtonClicked =
  (direction: 'backward' | 'forward') => () => {
    sendDigitalDataEvent({
      event: {
        name: 'click',
        category: 'gallery navigate',
        label: 'click',
        action: direction,
      },
    })

    datadogRum.addAction(Actions.CLICK_MULTIPLE_NAV_IMAGE_BUTTON, {
      service: Context.SEARCH_MULTIPLE_IMAGES,
      squad: Context.SQUAD,
      direction,
    })
  }

const handleCarouselImageViewed = (
  images: SearchVehicleImage[],
  index: number,
) => {
  const imageTitle = images[index].title
  sendDigitalDataEvent({
    event: {
      name: 'load',
      category: 'thumbnail gallery',
      label: imageTitle,
      action: index + 1,
    },
  })

  datadogRum.addAction(Actions.VIEWED_MULTIPLE_VEHICLE_CARD_IMAGE, {
    service: Context.SEARCH_MULTIPLE_IMAGES,
    squad: Context.SQUAD,
    label: imageTitle,
    imageIndex: index,
  })
}

const handleCarouselImageClicked = (
  images: SearchVehicleImage[],
  index: number,
) => {
  const imageTitle = images[index].title
  datadogRum.addAction(Actions.CLICK_MULTIPLE_VEHICLE_CARD_IMAGE, {
    service: Context.SEARCH_MULTIPLE_IMAGES,
    squad: Context.SQUAD,
    label: imageTitle,
    imageIndex: index,
  })
}

export function VehicleImageCarousel({
  images,
  isPending,
  isAvailable,
  productDetailUrl,
  ctaTitle,
  imageWithMorePhotosCta: imageTitleWithMorePhotosCta,
  imagesToDisplay: imageTitlesFilter,
}: VehicleImageCarouselProps) {
  const prevRef = useRef<HTMLButtonElement>(null)
  const nextRef = useRef<HTMLButtonElement>(null)
  const paginationRef = useRef<HTMLUListElement>(null)

  const [isFirstImageLoaded, setIsFirstImageLoaded] = useState(false)
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  const imagesToShow = imageTitlesFilter
    ? images.filter((image) =>
        imageTitlesFilter.includes(image.title as AdditionalImageTitle),
      )
    : images

  useEffect(() => {
    setIsInitialLoad(false)
  }, [])

  const renderCarouselItem = (
    { url, title }: SearchVehicleImage,
    index: number,
  ) => (
    <SwiperSlide
      aria-roledescription="slide"
      tag="div"
      key={url}
      className={styles.carouselSlide}
    >
      <a
        data-testid={`pdp-url-${index}`}
        href={productDetailUrl}
        aria-label={`${ctaTitle} ${title}`}
        onClick={() => handleCarouselImageClicked(images, index)}
      >
        {!isFirstImageLoaded ? (
          <HoldingImage
            isAvailable={isAvailable}
            className={styles.holdingImage}
          />
        ) : null}
        <div className={styles.carouselImageWrapper}>
          <Image
            src={url}
            width={391}
            height={220}
            alt={title}
            className={cx(
              styles.carouselImage,
              !isFirstImageLoaded && styles.hidden,
            )}
            onLoadingComplete={
              index === 0 ? () => setIsFirstImageLoaded(true) : undefined
            }
          />
          {title === imageTitleWithMorePhotosCta ? (
            <div
              className={cx(
                styles.morePhotosBackground,
                !isFirstImageLoaded && styles.hidden,
              )}
              aria-hidden="true"
            >
              <div className={styles.morePhotosCTA}>More photos</div>
            </div>
          ) : null}
        </div>
      </a>
    </SwiperSlide>
  )

  return (
    <div className={styles.vehicleImageCarouselContainer}>
      {isPending ? (
        <HoldingImage />
      ) : (
        <>
          <Swiper
            className={styles.vehicleImageCarousel}
            draggable={true}
            wrapperTag="div"
            onSlideChange={(swiper) => {
              swiper.slides.forEach((slide, slideIndex) => {
                const isActiveSlide = slideIndex === swiper.activeIndex
                const ariaHidden = isActiveSlide ? 'false' : 'true'
                slide.setAttribute('aria-hidden', ariaHidden)
                const slideLink = slide.querySelector('a')
                if (isActiveSlide) {
                  slideLink?.removeAttribute('tabindex')
                } else {
                  slideLink?.setAttribute('tabindex', '-1')
                }
              })
            }}
            onSlideChangeTransitionEnd={(swiper) => {
              if (!isInitialLoad) {
                handleCarouselImageViewed(images, swiper.realIndex)
              }
            }}
            a11y={{
              slideLabelMessage: 'Image {{index}} of {{slidesLength}}',
            }}
            navigation={{
              nextEl: nextRef.current,
              prevEl: prevRef.current,
            }}
            pagination={{
              el: paginationRef.current,
              bulletClass: styles.carouselBullet,
              bulletActiveClass: styles.carouselBulletActive,
              renderBullet: (index, className) =>
                `<li class="${className}">${index + 1}</li>`,
            }}
            loop={true}
            aria-label="car images"
            aria-roledescription="carousel"
            aria-atomic="false"
            aria-live="polite"
          >
            {imagesToShow.map((image, index) =>
              renderCarouselItem(image, index),
            )}
          </Swiper>

          <div
            className={cx(
              styles.carouselNav,
              !isFirstImageLoaded && styles.hidden,
            )}
          >
            <button
              type="button"
              className={styles.carouselNavButton}
              ref={prevRef}
              aria-label="Previous image"
              onClick={handleNavigateButtonClicked('backward')}
            >
              <SvgIcon
                name="chevron-left"
                color="pureWhite"
                size={24}
                className={styles.carouselNavIcon}
              />
            </button>

            <ul
              className={styles.paginationIndicators}
              aria-hidden="true"
              ref={paginationRef}
            />

            <button
              type="button"
              className={styles.carouselNavButton}
              ref={nextRef}
              aria-label="Next image"
              onClick={handleNavigateButtonClicked('forward')}
            >
              <SvgIcon
                name="chevron-right"
                color="pureWhite"
                size={24}
                className={styles.carouselNavIcon}
              />
            </button>
          </div>
        </>
      )}
    </div>
  )
}
